<template>
  <a-layout style="min-height: 100vh">
    <a-layout-sider v-model="collapsed" collapsible>
      <div class="logo">
        <img :src="logoImage" alt="最强牛人 Logo" />
        <span v-if="!collapsed">最强牛人</span>
      </div>
      <a-menu
        theme="dark"
        mode="inline"
        :selected-keys="selectedKeys"
        :open-keys="openKeys"
        @openChange="onOpenChange"
      >
        <template v-for="item in routes">
          <template v-if="!item.hidden">

            <a-menu-item 
              v-if="isSingleItem(item)" 
              :key="resolveKey(item)"
              @click="handleSingleClick(item)"
            >
              <a-icon :type="getIcon(item)" />
              <span>{{ getTitle(item) }}</span>
            </a-menu-item>

            <a-sub-menu 
              v-else 
              :key="item.path"
            >
              <span slot="title">
                <a-icon :type="item.meta && item.meta.icon ? item.meta.icon : 'appstore'" />
                <span>{{ item.meta && item.meta.title ? item.meta.title : '未命名' }}</span>
              </span>
              <template v-for="child in item.children">
                <a-menu-item v-if="!child.hidden" :key="resolvePath(item.path, child.path)"
                  @click="handleMenuClick(item.path, child.path)">
                  <span>{{ child.meta && child.meta.title ? child.meta.title : child.name }}</span>
                </a-menu-item>
              </template>
            </a-sub-menu>

          </template>
        </template>
      </a-menu>
    </a-layout-sider>

    <a-layout>
      <a-layout-header style="background: #fff; padding: 0 20px; display:flex; justify-content:space-between; align-items:center;">
        <a-icon 
          class="trigger" 
          :type="collapsed ? 'menu-unfold' : 'menu-fold'" 
          @click="() => (collapsed = !collapsed)" 
          style="font-size: 18px; cursor: pointer;"
        />
        <a-dropdown>
          <a class="ant-dropdown-link" @click="e => e.preventDefault()">
            管理员 <a-icon type="down" />
          </a>
          <a-menu slot="overlay">
            <a-menu-item @click="handleLogout">退出登录</a-menu-item>
          </a-menu>
        </a-dropdown>
      </a-layout-header>

      <a-layout-content style="margin: 16px; background: #fff; min-height: 280px;">
        <router-view />
      </a-layout-content>
    </a-layout>
    
  </a-layout>
</template>

<script>
  import { mapState } from 'vuex'
  import logoImage from '@/assets/logo.png'

  export default {
    data() {
      return {
        logoImage,
        collapsed: false,
        // 当前选中的菜单 key 列表（一般只有一个）
        selectedKeys: [],
        // 当前展开的父级菜单 key 列表，跟随路由自动维护，
        // 同时也允许用户手动点击展开/折叠
        openKeys: []
      }
    },
    computed: {
      ...mapState({
        routes: state => state.routes
      })
    },
    watch: {
      // 路由变化时同步选中项与展开项
      $route: {
        immediate: true,
        handler() {
          this.syncMenuState();
        }
      },
      // 路由数据异步加载（动态路由场景），加载完成后再同步一次
      routes: {
        handler() {
          this.syncMenuState();
        },
        deep: true
      }
    },
    methods: {
      isSingleItem(item) {
        // 1. 如果没有 children -> 单菜单
        if (!item.children || item.children.length === 0) {
          return true;
        }
        // 2. 如果是根路径 "/" 且只有一个子节点 -> 也是单菜单 (Dashboard情况)
        if (item.path === '/' && item.children.length === 1) {
          return true;
        }
        return false;
      },

      getTitle(item) {
        if (item.path === '/' && item.children && item.children.length === 1) {
          return item.children[0].meta?.title || item.children[0].title;
        }
        return item.meta?.title || item.title;
      },

      getIcon(item) {
        if (item.path === '/' && item.children && item.children.length === 1) {
          return item.children[0].meta?.icon || item.children[0].icon || 'desktop';
        }
        return item.meta?.icon || item.icon || 'desktop';
      },

      resolveKey(item) {
        if (item.path === '/' && item.children && item.children.length === 1) {
          return this.resolvePath(item.path, item.children[0].path);
        }
        return item.path;
      },

      handleSingleClick(item) {
        let targetPath = item.path;
        if (item.path === '/' && item.children && item.children.length === 1) {
          targetPath = this.resolvePath(item.path, item.children[0].path);
        }
        if (this.$route.path !== targetPath) {
          this.$router.push(targetPath);
        }
      },

      resolvePath(basePath, routePath) {
        if (!routePath) return basePath;
        if (basePath.endsWith('/')) return basePath + routePath;
        return basePath + '/' + routePath;
      },

      handleMenuClick(basePath, routePath) {
        const fullPath = this.resolvePath(basePath, routePath);
        if (this.$route.path !== fullPath) {
          this.$router.push(fullPath);
        }
      },

      handleLogout() {
        this.$store.dispatch('Logout');
      },

      // 用户手动展开/折叠父菜单时，记录当前 openKeys
      onOpenChange(keys) {
        this.openKeys = keys;
      },

      // 根据当前路由路径，反推应当选中的菜单 key 与应当展开的父菜单 key
      syncMenuState() {
        const currentPath = this.$route.path;
        if (!currentPath) {
          return;
        }

        // 1. 选中项：默认就是当前路径
        this.selectedKeys = [currentPath];

        // 2. 展开项：在 routes 中匹配 children 命中的父级 path
        if (!this.routes || this.routes.length === 0) {
          return;
        }
        const parentKeys = [];
        this.routes.forEach(item => {
          if (item.hidden || this.isSingleItem(item) || !item.children) {
            return;
          }
          const matched = item.children.some(child => {
            if (child.hidden) return false;
            const fullPath = this.resolvePath(item.path, child.path);
            return fullPath === currentPath;
          });
          if (matched && parentKeys.indexOf(item.path) === -1) {
            parentKeys.push(item.path);
          }
        });

        // 合并已有的 openKeys，避免覆盖用户手动展开的其他目录
        const merged = parentKeys.slice();
        this.openKeys.forEach(key => {
          if (merged.indexOf(key) === -1) {
            merged.push(key);
          }
        });
        this.openKeys = merged;
      }
    }
  }
</script>

<style scoped>
.logo {
  height: 48px;
  margin: 16px;
  color: #fff;
  font-size: 18px;
  font-weight: bold;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  white-space: nowrap;
}
.logo img {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  object-fit: cover;
  flex-shrink: 0;
}
</style>
